import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiClock, FiPackage, FiArrowLeft, FiMapPin, FiPhone, FiMail } from 'react-icons/fi';
import { authAPI } from '../api/auth';
import { ordersAPI, OrderDetail as OrderDetailType, OrderItem as OrderItemType, SelectedOption } from '../api/orders';
import { formatCurrency } from '../utils/currency';
import { getLocalizedField } from '../utils/localize';
import { getStatusLabel } from '../utils/orderStatus';
import type { SupportedLanguage } from '../i18n';
import { resolveUploadUrl } from '../utils/imageUrl';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-bottom: 2rem;
  transition: all 0.2s;

  &:hover {
    background-color: var(--primary-color);
    color: var(--white);
  }
`;

const OrderHeader = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const OrderTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderNumber = styled.h1`
  font-size: 1.5rem;
  color: var(--text-color);
`;

const OrderStatus = styled.span<{ $status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-light, #FEF3C7)';
      case 'confirmed':
        return 'var(--info-light, #DBEAFE)';
      case 'ready':
        return 'var(--success-light, #E8F5E9)';
      case 'completed':
        return 'var(--success-color, #44BB44)';
      case 'preparing':
      case 'in_preparation':
        return 'var(--preparing-light, #FFE0B2)';
      case 'ready_for_pickup':
        return 'var(--success-light, #E8F5E9)';
      case 'cancelled':
        return 'var(--danger-light, #FFEBEE)';
      default:
        return 'var(--gray-light)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-dark, #D97706)';
      case 'confirmed':
        return 'var(--info-dark, #1E40AF)';
      case 'ready':
        return 'var(--success-dark, #2E7D32)';
      case 'completed':
        return 'var(--white)';
      case 'preparing':
      case 'in_preparation':
        return 'var(--preparing-dark, #E65100)';
      case 'ready_for_pickup':
        return 'var(--success-dark, #2E7D32)';
      case 'cancelled':
        return 'var(--danger-color, #C62828)';
      default:
        return 'var(--text-color)';
    }
  }};
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoSection = styled.div``;

const InfoLabel = styled.div`
  color: var(--gray-dark);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoValue = styled.div`
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 500;
`;

const ItemsSection = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: var(--text-color);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ItemCard = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-light);
  border-radius: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gray-light);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-dark);

  @media (max-width: 768px) {
    width: 100%;
    height: 150px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ItemName = styled.h3`
  font-size: 1rem;
  color: var(--text-color);
  margin-bottom: 0.25rem;
`;

const ItemInfo = styled.div`
  font-size: 0.875rem;
  color: var(--gray-dark);
`;

const ItemOptions = styled.div`
  font-size: 0.8rem;
  color: var(--gray-dark);
  margin-top: 0.25rem;
`;

const ItemPrice = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    text-align: left;
    margin-top: 0.5rem;
  }
`;

const Price = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
`;

const Quantity = styled.div`
  font-size: 0.875rem;
  color: var(--gray-dark);
`;

const SummarySection = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--gray-light);

  &:last-child {
    border-bottom: none;
    padding-top: 1rem;
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const LoadingState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
`;

const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [order, setOrder] = useState<OrderDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<any>({});

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const fetchOrderDetail = async () => {
      if (!orderId) return;

      try {
        const orderData = await ordersAPI.getOrderDetail(parseInt(orderId));
        setOrder(orderData);

        // Fetch product details for each item. Fetches run concurrently
        // (Promise.allSettled) with a per-item timeout so one slow/hung
        // product lookup can't stall the whole order detail screen.
        const productDetails: any = {};
        await Promise.allSettled(orderData.items.map(async (item) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/products/${item.product_id}`, { signal: controller.signal });
            if (response.ok) {
              productDetails[item.product_id] = await response.json();
            }
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              console.warn('Product detail fetch timed out for product', item.product_id);
            } else {
              console.error('Failed to fetch product details:', error);
            }
          } finally {
            clearTimeout(timeoutId);
          }
        }));
        setProducts(productDetails);
      } catch (error) {
        console.error('Failed to fetch order details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderId, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5); // Get HH:MM from HH:MM:SS
  };

  // Resolves the item's display name, preferring the live product fetch
  // (tier 1, also supplies image_url) and falling back to the server-
  // snapshotted product_name/product_name_zh (backend/app/db/models.py's
  // OrderItem.product_name/product_name_zh properties) when the live
  // /products/{id} lookup misses -- e.g. the product has since been
  // deactivated (active==True filtered, never hard-deleted once it has
  // order history). Mirrors AccountOrders.tsx's getQbItemDisplayName for
  // Bubble Tea/Quick Bites order history.
  const getItemDisplayName = (item: OrderItemType, product: any): string => {
    if (product) {
      return getLocalizedField(product, 'name', language) as string;
    }
    if (language === 'zh' && item.product_name_zh) {
      return item.product_name_zh;
    }
    if (item.product_name) {
      return item.product_name;
    }
    return t('orderConfirmation.unknownProduct', { id: item.product_id });
  };

  // Renders a customizable product's selected options as "Category: Item, ..."
  // text, matching AdminQBOrders' muted key:value convention. Returns '' when
  // there are no selections so callers can skip rendering entirely.
  const formatSelectedOptions = (options: SelectedOption[] | null | undefined, language: SupportedLanguage): string => {
    if (!options || options.length === 0) return '';
    return options
      .map(opt => `${(getLocalizedField(opt, 'category_name', language) as string | null) ?? t('orderDetail.optionCategoryFallback')}: ${getLocalizedField(opt, 'item_name', language)}`)
      .join(', ');
  };

  const getStatusMessage = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('orderDetail.statusMessage.pending');
      case 'confirmed':
        return t('orderDetail.statusMessage.confirmed');
      case 'preparing':
        return t('orderDetail.statusMessage.preparing');
      case 'in_preparation':
        return t('orderDetail.statusMessage.in_preparation');
      case 'ready':
        return t('orderDetail.statusMessage.ready');
      case 'ready_for_pickup':
        return t('orderDetail.statusMessage.ready_for_pickup');
      case 'completed':
        return t('orderDetail.statusMessage.completed');
      case 'cancelled':
        return t('orderDetail.statusMessage.cancelled');
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>{t('orderConfirmation.loading')}</LoadingState>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <LoadingState>{t('orderConfirmation.orderNotFound')}</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/orders')}>
        <FiArrowLeft />
        {t('orderDetail.backToOrders')}
      </BackButton>

      <OrderHeader>
        <OrderTitle>
          <OrderNumber>{t('accountOrders.orderNumberLabel', { number: order.order_number })}</OrderNumber>
          <OrderStatus $status={order.status.toLowerCase()}>
            {getStatusLabel(order.status, t)}
          </OrderStatus>
        </OrderTitle>

        <div style={{ marginBottom: '1rem', color: 'var(--gray-dark)' }}>
          {getStatusMessage(order.status)}
        </div>

        <OrderInfo>
          <InfoSection>
            <InfoLabel>
              <FiCalendar />
              {t('orderDetail.orderDateLabel')}
            </InfoLabel>
            <InfoValue>{formatDate(order.created_at)}</InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <FiClock />
              {t('orderDetail.pickupTimeLabel')}
            </InfoLabel>
            <InfoValue>
              {t('orderDetail.pickupDateTimeValue', { date: formatDate(order.pickup_date), time: formatTime(order.pickup_time) })}
            </InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <FiMapPin />
              {t('orderDetail.customerNameLabel')}
            </InfoLabel>
            <InfoValue>{order.customer_name}</InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <FiPhone />
              {t('orderDetail.phoneLabel')}
            </InfoLabel>
            <InfoValue>{order.customer_phone}</InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <FiMail />
              {t('orderDetail.emailLabel')}
            </InfoLabel>
            <InfoValue>{order.customer_email}</InfoValue>
          </InfoSection>
        </OrderInfo>

        {order.special_instructions && (
          <InfoSection style={{ marginTop: '1.5rem' }}>
            <InfoLabel>{t('orderConfirmation.specialInstructions')}</InfoLabel>
            <InfoValue>{order.special_instructions}</InfoValue>
          </InfoSection>
        )}
      </OrderHeader>

      <ItemsSection>
        <SectionTitle>
          <FiPackage />
          {t('orderConfirmation.orderItems')}
        </SectionTitle>
        <ItemsList>
          {order.items.map((item) => {
            const product = products[item.product_id];
            const optionsStr = formatSelectedOptions(item.selected_options, language);
            return (
              <ItemCard key={item.order_item_id}>
                <ItemImage>
                  {product?.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product?.name || 'Product'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ) : (
                    t('orderDetail.noImage')
                  )}
                </ItemImage>
                <ItemDetails>
                  <ItemName>{getItemDisplayName(item, product)}</ItemName>
                  <ItemInfo>
                    {item.size_id && `${t('orderDetail.sizeLabel')} ${item.size_id} • `}
                    {t('orderConfirmation.quantityLabel')} {item.quantity}
                  </ItemInfo>
                  {optionsStr && <ItemOptions>{optionsStr}</ItemOptions>}
                  {item.custom_image_url && (
                    <a
                      href={resolveUploadUrl(item.custom_image_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-item-photo"
                      title={t('orderDetail.cakePhotoLabel')}
                      style={{ display: 'inline-block', marginTop: '6px' }}
                    >
                      <img
                        src={resolveUploadUrl(item.custom_image_url)}
                        alt={t('orderDetail.cakePhotoAlt')}
                        style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                      />
                    </a>
                  )}
                </ItemDetails>
                <ItemPrice>
                  <Price>{formatCurrency(item.subtotal)}</Price>
                  <Quantity>{t('orderDetail.eachPrice', { price: formatCurrency(item.unit_price) })}</Quantity>
                </ItemPrice>
              </ItemCard>
            );
          })}
        </ItemsList>
      </ItemsSection>

      <SummarySection>
        <SectionTitle>{t('orderDetail.orderSummary')}</SectionTitle>
        <SummaryRow>
          <span>{t('orderDetail.total')}</span>
          <span>{formatCurrency(order.total_amount)}</span>
        </SummaryRow>
      </SummarySection>
    </Container>
  );
};

export default OrderDetailPage;